import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { TextField } from '@mui/material';

import { locales } from '../../../i18n';
import { closeModalOnClickOutside } from '../../../helpers/closeModalOnClickOutside';
import { 
  InsuranceParameters, 
  SetInsuranceParameters, 
  FormInsurance, 
  IInsurance, 
  SetInsurance 
} from '../../../types';
import { dateConvert } from '../../../helpers/dateToIso';
import { 
  TouristModalWrapper, 
  PopoverWrapper, 
  TouristElement, 
  TouristElementHeader,
  TouristOrder,
  DeleteButton,
  DeleteText,
  AddTourist,
  PopoverModal,
  TouristsWrapper 
} from '../style';

type Props = {
  insurance: IInsurance; 
  setInsurance: SetInsurance;
  insuranceParameters?: InsuranceParameters;
  setInsuranceParameters?: SetInsuranceParameters;
  anchorEl: any;  
};

const TouristsModal: React.FC<Props> = ({
  insurance,
  setInsurance,
  insuranceParameters,
  setInsuranceParameters,
  anchorEl, 
}) => {
  const {
    t,
    i18n: { language },
  } = useTranslation();
  const { selectedTourists, modalsVisibility } = insuranceParameters;
  const [popoverWidth, setPopoverWidth] = useState(0);

  const closePopOver = () => {
    closeModalOnClickOutside(
      insuranceParameters,
      setInsuranceParameters,
      'tourists'
    );
  };  

  useEffect(() => {
    displayWindowSize();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedTourists]);

  const displayWindowSize = () => {
    const width = document.getElementById(anchorEl.id).offsetWidth || 0;
    setPopoverWidth(width);
  };

  window.addEventListener('resize', displayWindowSize);

  const addtourist = () => {
    closeModalOnClickOutside(
      insuranceParameters,
      setInsuranceParameters,
      'tourists'
    );
    selectedTourists.push({
      birthDate: '',
      country: '',
      city: '',
      fieldsCompleted: false
    });
    setInsuranceParameters({
      ...insuranceParameters,
      modalsVisibility: {
        ...modalsVisibility,
        tourists: true,
      },
    });    
    
    setInsurance({
      ...insurance,
      details: {
        ...insurance.details,
        insurance_form: `${
          (insurance.beneficiary.length + 1) > 1 
            ? FormInsurance.Group : FormInsurance.Individual
        }`
      },           
      beneficiary: [
        ...insurance.beneficiary,
        {
          first_name: '',
          last_name: '',
          date_of_birth: '',
          country: '',
          city: '',
          address: '',
          passport_series_number: '',
          idnp: '',
          email: '',
          phone: ''
        }
      ]     
    })
  };

  const deleteTourist = (index: number) => {
    const withoutRemoved = [...selectedTourists];
    const beneficiaries = [...insurance.beneficiary];
    withoutRemoved.splice(index, 1);
    beneficiaries.splice(index, 1);

    setInsuranceParameters({
      ...insuranceParameters,
      selectedTourists: withoutRemoved,
    });
    setInsurance({
      ...insurance,
      details: {
        ...insurance.details,
        insurance_form: `${beneficiaries.length > 1 ? FormInsurance.Group : FormInsurance.Individual}`
      },
      beneficiary: beneficiaries
    });
  };

  const addBirthDate = (birthDate: string, index: number) => {
    const allTourists = [...selectedTourists];
    const beneficiaries = [...insurance.beneficiary];    
    allTourists[index].birthDate = birthDate;
    beneficiaries[index].date_of_birth = dateConvert(new Date(birthDate));  
    
    setInsuranceParameters({
      ...insuranceParameters,
      selectedTourists: allTourists,
    });

    setInsurance({
      ...insurance,
      beneficiary: beneficiaries
    });
  };

  return (
    <TouristModalWrapper id='tourist-wrapper'>
      <PopoverModal
        open={modalsVisibility.tourists}
        anchorReference='anchorEl'
        anchorEl={anchorEl}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
        onClose={closePopOver}
      >
        <PopoverWrapper width={popoverWidth}>
          <TouristsWrapper>
            {insurance.beneficiary.map((_, index) => {
              return (
                <TouristElement key={index}>
                  <TouristElementHeader>
                    <TouristOrder>{`${t('tourist')} ${
                      index + 1
                    }`}</TouristOrder>
                    <DeleteButton
                      variant='text'
                      onClick={() => deleteTourist(index)}
                    >
                      <DeleteText>{t('delete')}</DeleteText>
                    </DeleteButton>
                  </TouristElementHeader>
                  <LocalizationProvider
                    dateAdapter={AdapterDateFns}
                    adapterLocale={locales[language]}
                  >
                    <DatePicker
                      label={t('birth_date')}
                      value={insurance.beneficiary[index].date_of_birth}
                      onChange={(date) => addBirthDate(String(date), index)}
                      renderInput={(params) => (
                        <TextField style={{ width: '100%' }} {...params} />
                      )}
                      disableMaskedInput
                    />
                  </LocalizationProvider>
                </TouristElement>
              );
            })}
            <AddTourist variant='outlined' onClick={addtourist}>
              {t('add_tourist')}
            </AddTourist>
          </TouristsWrapper>
        </PopoverWrapper>
      </PopoverModal>
    </TouristModalWrapper>
  );
};

export default TouristsModal;
